import { DEFAULT_AI_FALLBACK_MODEL_ID, isFreeTierModelId } from "@winche/shared";

export type ModelKind = "text" | "vision";

/**
 * A production write to `config/global` that moves AI calls to another model
 * (ux-admin-02): the active text or vision model, a paid fallback, or a
 * free-tier toggle. Every one goes through a confirmation with its scope, and
 * the panel offers «Revert» to the value it replaced.
 */
export type ModelChange =
  | { type: "active"; kind: ModelKind; from: string | null; to: string }
  | { type: "fallback"; kind: ModelKind; from: string | null; to: string | null }
  | { type: "freeTier"; kind: ModelKind; model: string | null; from: boolean; to: boolean };

/**
 * Whether a model id is free tier. The panel passes `isFreeModelId`
 * (`lib/or-models.ts`), which also knows the catalog's price-0 models.
 */
export type IsFreeModel = (modelId: string) => boolean;

/** The model a change routes calls to, when it routes them to one model. */
function targetModel(change: ModelChange): string | null {
  if (change.type === "freeTier") return change.to ? change.model : null;
  return change.to;
}

/**
 * The change sends prompts to OpenRouter's free endpoints, which may keep
 * them (gap-privacy-data-flows-03): the dialog warns and asks to type FREE,
 * and the callable needs `acknowledgeFreeTierRetention`.
 */
export function changeNeedsFreeTierAck(change: ModelChange, isFree: IsFreeModel = isFreeTierModelId): boolean {
  const target = targetModel(change);
  return target != null && (isFreeTierModelId(target) || isFree(target));
}

/** The change that undoes `change`. */
export function revertOf(change: ModelChange): ModelChange | null {
  if (change.type === "freeTier") return { ...change, from: change.to, to: change.from };
  if (change.type === "active") return change.from ? { ...change, from: change.to, to: change.from } : null;
  return { ...change, from: change.to, to: change.from };
}

const KIND_CALLS: Record<ModelKind, string> = {
  text: "Every AI call for every user (assistant, CVs, letters, reviews, prep; evaluate_job keeps its own model)",
  vision: "Every document call for every user (CV parsing from a PDF or photo, the visual CV review)",
};

export function modelChangeTitle(change: ModelChange): string {
  const what = change.kind === "text" ? "text" : "vision";
  if (change.type === "active") return `Switch the ${what} model to ${change.to}?`;
  if (change.type === "fallback") return change.to ? `Set the paid ${what} fallback to ${change.to}?` : `Remove the paid ${what} fallback?`;
  return change.to ? `Use the free tier for ${what}?` : `Stop using the free tier for ${what}?`;
}

/** What changes and for whom, the first paragraph of the dialog. */
export function modelChangeScope(change: ModelChange): string {
  const propagation = "This instance applies it now; the others pick it up within 5 minutes.";
  if (change.type === "active") {
    const billing = change.to.startsWith("anthropic/")
      ? ""
      : " It is outside the daily Haiku cap and is billed on OpenRouter credits.";
    return `${KIND_CALLS[change.kind]} moves from ${change.from ?? "the default model"} to ${change.to}.${billing} ${propagation}`;
  }
  if (change.type === "fallback") {
    const to = change.to ?? `the default fallback (${DEFAULT_AI_FALLBACK_MODEL_ID})`;
    const from = change.from ?? `the default fallback (${DEFAULT_AI_FALLBACK_MODEL_ID})`;
    return `A ${change.kind} call that fails moves to ${to} instead of ${from}. ${propagation}`;
  }
  const model = change.model ?? "the :free model";
  return change.to
    ? `${KIND_CALLS[change.kind]} runs on ${model} through OpenRouter's free quota and moves to the paid fallback on 429, 404 or timeout. ${propagation}`
    : `${KIND_CALLS[change.kind]} skips ${model} and runs on its paid model. ${propagation}`;
}

/** The retention warning of a change that reaches free endpoints, or null. */
export function freeTierRetentionWarning(change: ModelChange, isFree: IsFreeModel = isFreeTierModelId): string | null {
  if (!changeNeedsFreeTierAck(change, isFree)) return null;
  return (
    `${targetModel(change)} is served by OpenRouter's free endpoints, which may keep prompts and train on them: ` +
    "CVs, job postings and recruiter emails would go there. Requests ask for endpoints without data collection " +
    "(data_collection: deny), so a free model whose endpoints all keep or train on prompts is never used: " +
    "its calls move to the paid fallback."
  );
}

/** One line for the «Changed … · Revert» notice. */
export function modelChangeSummary(change: ModelChange): string {
  const what = change.kind === "text" ? "Text" : "Vision";
  if (change.type === "active") return `${what} model: ${change.from ?? "default"} → ${change.to}.`;
  if (change.type === "fallback") return `Paid ${change.kind} fallback: ${change.from ?? "default"} → ${change.to ?? "default"}.`;
  return `${what} free tier: ${change.from ? "on" : "off"} → ${change.to ? "on" : "off"}.`;
}
